import React from 'react';
import { Box, VStack, Text, Spinner, HStack, Button, Select } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import NotificationItem from './NotificationItem';
import type { NotificationItem as Item } from '../../types/notification';

export default function NotificationList({
  items,
  isLoading,
  isError,
  onRetry,
  onItemOpen,
  onItemToggleRead,
  onItemArchive,
  status,
  onStatusChange,
  emptyLabel,
  maxHeight,
  highlightedId,
}: {
  items: Item[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  /** Opens the row (the caller routes the role-resolved target). */
  onItemOpen: (item: Item) => void;
  /** Toggles read/unread only. Must never navigate. */
  onItemToggleRead: (item: Item) => void;
  onItemArchive: (item: Item) => void;
  status?: 'all' | 'unread' | 'archived';
  onStatusChange?: (s: 'all' | 'unread' | 'archived') => void;
  emptyLabel?: string;
  maxHeight?: string;
  /** Row selected by the `?notification=` deep link. */
  highlightedId?: string | null;
}): JSX.Element {
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <HStack justify="center" py={6}>
        <Spinner size="sm" />
      </HStack>
    );
  }
  if (isError) {
    return (
      <VStack py={6} spacing={2}>
        <Text color="red.500" fontSize="sm">
          {t('notification.loadError')}
        </Text>
        {onRetry && (
          <Button size="xs" onClick={onRetry}>
            {t('common.retry')}
          </Button>
        )}
      </VStack>
    );
  }

  return (
    <VStack align="stretch" spacing={0} maxH={maxHeight} overflowY="auto">
      {onStatusChange && (
        <Box px={3} py={2} borderBottomWidth="1px">
          <Select
            size="xs"
            value={status}
            onChange={(e) => onStatusChange(e.target.value as 'all' | 'unread' | 'archived')}
          >
            <option value="all">{t('notification.filterAll')}</option>
            <option value="unread">{t('notification.filterUnread')}</option>
            <option value="archived">{t('notification.filterArchived')}</option>
          </Select>
        </Box>
      )}
      {items.length === 0 && (
        <Text py={6} textAlign="center" color="gray.500" fontSize="sm">
          {emptyLabel ?? t('notification.empty')}
        </Text>
      )}
      {items.map((item) => (
        <NotificationItem
          key={item.id}
          item={item}
          highlighted={highlightedId === item.id}
          onClick={() => onItemOpen(item)}
          onToggleRead={() => onItemToggleRead(item)}
          onArchive={() => onItemArchive(item)}
        />
      ))}
    </VStack>
  );
}
